import { useEffect } from "react";
import { DateTime } from "luxon";
import useRoutePlannerStore from "../state/route-planner-store";

/**
 * Hook that manages centralized time synchronization for the route planner.
 * Updates the store's syncedCurrentTime at exact minute boundaries.
 *
 * This ensures all components that depend on current time (current time indicator,
 * service time adjustments, travel time adjustments) update at exactly the same moment.
 *
 * Note: While updates happen at minute boundaries, calculations use the exact time
 * including seconds for precise service/travel time tracking.
 *
 * Call this hook once at the top level of the route planner (e.g., in the main page component).
 * All child components should read `syncedCurrentTime` from the store instead of using local state.
 */
export const useTimeSynchronization = () => {
  const setSyncedCurrentTime = useRoutePlannerStore(
    (state) => state.setSyncedCurrentTime,
  );

  useEffect(() => {
    // Update immediately on mount
    setSyncedCurrentTime(DateTime.now());

    let timeoutId: NodeJS.Timeout | null = null;

    // Self-correcting timeout function that always re-syncs with actual clock time
    const scheduleNextUpdate = () => {
      const now = DateTime.now();
      const nextMinute = now.startOf("minute").plus({ minutes: 1 });
      // Add 50ms buffer to ensure we're past the minute boundary
      const msUntilNextMinute = nextMinute.diff(now).milliseconds + 50;

      timeoutId = setTimeout(() => {
        setSyncedCurrentTime(DateTime.now());
        // Recursively schedule the next update - this re-syncs with actual clock time
        // even if the previous timeout was delayed (e.g., due to inactive tab)
        scheduleNextUpdate();
      }, msUntilNextMinute);
    };

    // Start the self-correcting update cycle
    scheduleNextUpdate();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [setSyncedCurrentTime]);
};

export default useTimeSynchronization;
